import PropTypes from 'prop-types';
import React from 'react';
import { Star, ChevronUp, ChevronDown } from 'lucide-react';
import { Link, useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const ASSET_NESTED_ITEMS = ['Asset', 'Maps', 'Beacon', 'Tracker'];
const TIMESHEET_NESTED_ITEMS = ['Timesheets', 'BillLogs'];
const MESSAGE_NESTED_ITEMS = ['TextMessage', 'Whatsapp'];
const INVOICE_NESTED_ITEMS = ['Invoices', 'Purchase'];

const SideNavItem = ({
  selectedItem,
  mobileOpen,
  handleListItemClick,
  handleLargeDevice,
  primaryText,
  icon,
  expandNestedItem,
  to,
  isCollapsed,
  taskNotificationCount,
  qualsNotificationCount
}) => {
  const history = useHistory();
  const location = useLocation();
  const isSelected = selectedItem === primaryText;

  const hasNestedItems =
    primaryText === 'Asset' ||
    primaryText === 'Timesheets and Logs' ||
    primaryText === 'Message' ||
    primaryText === 'Invoices';

  const nestedItemList =
    primaryText === 'Asset'
      ? ASSET_NESTED_ITEMS
      : primaryText === 'Timesheets and Logs'
      ? TIMESHEET_NESTED_ITEMS
      : primaryText === 'Message'
      ? MESSAGE_NESTED_ITEMS
      : INVOICE_NESTED_ITEMS;

  const isExpanded =
    expandNestedItem?.[
      primaryText === 'Asset'
        ? 'asset'
        : primaryText === 'Timesheets and Logs'
        ? 'timesheetAndLogs'
        : primaryText === 'Message'
        ? 'message'
        : 'invoices'
    ];

  const handleClick = () => {
    if (hasNestedItems) {
      history.push('/' + nestedItemList[0].toLowerCase());
    }

    if (isCollapsed && hasNestedItems) {
      // If collapsed, don't expand, just click (or maybe ignore if it's only a folder)
      handleListItemClick(primaryText);
      return;
    }
    // Original logic for handling clicks
    if (typeof handleListItemClick === 'function') {
      // Check if it's a function
      handleListItemClick(primaryText);
    } else if (typeof handleLargeDevice === 'function') {
      // Fallback to handleLargeDevice if mobileOpen is not passed or false
      handleLargeDevice(primaryText);
    }
  };

  const navItemClass = cn(
    'tw-flex tw-items-center tw-w-full tw-py-3 tw-text-sm tw-font-semibold tw-rounded-xl tw-transition-all tw-duration-200',
    isCollapsed ? 'tw-justify-center tw-px-0' : 'tw-justify-between tw-px-4',
    isSelected ? 'tw-bg-blue-50 tw-text-blue-600' : 'tw-text-gray-500 hover:tw-bg-gray-50 hover:tw-text-gray-900'
  );

  const iconClass = cn(
    'tw-flex-shrink-0 tw-h-5 tw-w-5 tw-transition-colors',
    !isCollapsed && 'tw-mr-3',
    isSelected ? 'tw-text-blue-600' : 'tw-text-gray-400 group-hover:tw-text-gray-600'
  );

  return (
    <li className="tw-list-none tw-mb-1">
      {hasNestedItems ? (
        <>
          <button
            onClick={handleClick}
            className={cn(navItemClass, 'tw-group focus:tw-outline-none')}
            title={isCollapsed ? primaryText : ''}
          >
            <div className="tw-flex tw-items-center">
              <span className={iconClass}>{icon}</span>
              {!isCollapsed && primaryText}
            </div>
            {!isCollapsed &&
              (isExpanded ? <ChevronUp className="tw-h-4 tw-w-4" /> : <ChevronDown className="tw-h-4 tw-w-4" />)}
          </button>

          {!isCollapsed && (
            <div
              className={cn(
                'tw-overflow-hidden tw-transition-all tw-duration-300 tw-ease-in-out',
                isExpanded ? 'tw-max-h-64 tw-opacity-100' : 'tw-max-h-0 tw-opacity-0'
              )}
            >
              <ul className="tw-mt-1 tw-space-y-1 tw-pl-12 tw-pr-2 tw-pb-2">
                {nestedItemList?.map((item) => {
                  const itemPath = '/' + item.toLowerCase();
                  const isSubItemSelected = location.pathname.includes(itemPath);

                  return (
                    <li key={item}>
                      <Link
                        to={itemPath}
                        className={cn(
                          'tw-flex tw-items-center tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-rounded-lg tw-transition-colors',
                          isSubItemSelected
                            ? 'tw-bg-blue-50 tw-text-blue-600'
                            : 'tw-text-gray-500 hover:tw-text-gray-900 hover:tw-bg-gray-50'
                        )}
                      >
                        <Star
                          className={cn(
                            'tw-mr-3 tw-h-3 tw-w-3 tw-transition-colors',
                            isSubItemSelected ? 'tw-text-blue-600' : 'tw-text-gray-300'
                          )}
                        />
                        {item}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      ) : (
        <Link
          to={to}
          onClick={handleClick}
          className={cn(navItemClass, 'tw-group')}
          title={isCollapsed ? primaryText : ''}
        >
          <div className="tw-flex tw-items-center">
            <span className={iconClass}>{icon}</span>

            <div className="tw-flex tw-flex-1 tw-items-center tw-justify-between">
              <span className="tw-mr-5">{!isCollapsed && primaryText}</span>

              {!isCollapsed && taskNotificationCount > 0 && (
                <span
                  className="tw-ml-auto tw-rounded-full tw-bg-red-500 tw-px-2 tw-py-0.5 tw-text-xs tw-font-bold tw-text-white"
                  aria-label={`${taskNotificationCount} unread tasks`}
                >
                  {taskNotificationCount}
                </span>
              )}

              {!isCollapsed && qualsNotificationCount > 0 && (
                <span
                  className="tw-ml-auto tw-rounded-full tw-bg-red-500 tw-px-2 tw-py-0.5 tw-text-xs tw-font-bold tw-text-white"
                  aria-label={`${qualsNotificationCount} pending qualifications`}
                >
                  {qualsNotificationCount}
                </span>
              )}
            </div>
          </div>
        </Link>
      )}
    </li>
  );
};

export default SideNavItem;

SideNavItem.propTypes = {
  primaryText: PropTypes.string,
  mobileOpen: PropTypes.bool,
  handleListItemClick: PropTypes.func,
  role: PropTypes.string,
  selectedItem: PropTypes.string,
  handleLargeDevice: PropTypes.func,
  icon: PropTypes.any,
  expandNestedItem: PropTypes.shape({
    timesheetAndLogs: PropTypes.bool,
    asset: PropTypes.bool,
    message: PropTypes.bool,
    invoices: PropTypes.bool
  }),
  to: PropTypes.string,
  isCollapsed: PropTypes.bool,
  taskNotificationCount: PropTypes.number,
  qualsNotificationCount: PropTypes.number
};
